import { useState, useRef, useEffect, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from './ui/button';
import { Bot, X, Send, Loader2, ShieldAlert } from 'lucide-react';
import { authHeaders } from '../contexts/AuthContext';
import { useLocation } from 'react-router-dom';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PAGE_HINTS = {
  '/dashboard': { label: 'Dashboard', hint: "I can help you prioritize tasks, review your week's progress, or draft project updates." },
  '/projects': { label: 'Projects', hint: "Need help with budgets, timelines, or task breakdowns? I've got you." },
  '/connections': { label: 'Connections', hint: "Looking for a collaborator? Describe what you need and I'll find the best match." },
  '/invoices': { label: 'Invoices', hint: "I can generate invoices from a description. Just tell me the project and line items." },
  '/contracts': { label: 'Contracts', hint: "Need a contract template? I can draft one based on your project terms." },
  '/files': { label: 'Files', hint: "I can help organize files, suggest naming conventions, or create shot lists." },
  '/payments': { label: 'Payments', hint: "Need payment reminders or a summary of outstanding balances? Ask me." },
  '/spectre': { label: 'Specter', hint: "Full chat mode. Ask me anything about your projects, team, or industry." },
  '/calendar': { label: 'Calendar', hint: "I can suggest schedule optimizations or draft call sheets for your shoots." },
  '/activity': { label: 'Activity', hint: "Want a summary of recent activity or team performance? I'll compile it." },
  '/settings': { label: 'Settings', hint: "Need help configuring your workspace or availability preferences?" },
  '/ainaa': { label: 'AINAA', hint: "I can help you understand your trust score and suggest ways to improve it." },
  '/analytics': { label: 'Analytics', hint: "Want deeper insights into your project metrics or team utilization?" },
};

function getPageContext(pathname) {
  if (pathname.startsWith('/projects/')) return { label: 'Project Detail', hint: "I know this project well. Ask me to generate tasks, budgets, call sheets, or crew suggestions." };
  for (const [path, ctx] of Object.entries(PAGE_HINTS)) {
    if (pathname.startsWith(path)) return ctx;
  }
  return { label: 'Slate', hint: "I'm Specter, your work companion. Ask me anything." };
}

export default function SpecterAssistant({ context, suggestedPrompts = [] }) {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [sessionId] = useState(() => `assist_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`);
  const [showBubble, setShowBubble] = useState(false);
  const [bubbleDismissed, setBubbleDismissed] = useState(false);
  const endRef = useRef(null);
  const inputRef = useRef(null);
  const location = useLocation();

  const pageCtx = useMemo(() => getPageContext(location.pathname), [location.pathname]);

  // Show context bubble on page change (max once per page)
  useEffect(() => {
    setBubbleDismissed(false);
    if (!open) {
      const timer = setTimeout(() => setShowBubble(true), 1200);
      const hide = setTimeout(() => setShowBubble(false), 7000);
      return () => { clearTimeout(timer); clearTimeout(hide); };
    }
  }, [location.pathname, open]);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  useEffect(() => {
    if (open) { inputRef.current?.focus(); setShowBubble(false); }
  }, [open]);

  const send = async (text) => {
    const msg = text || input.trim();
    if (!msg || loading) return;
    setInput('');
    setMessages(prev => [...prev, { role: 'user', content: msg }]);
    setLoading(true);
    try {
      const res = await fetch(`${API}/spectre/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({
          message: `[Context: User is on the ${pageCtx.label} page] ${msg}`,
          session_id: sessionId,
          context: context || {},
        }),
      });
      const data = await res.json();
      setMessages(prev => [...prev, { role: 'assistant', content: data.response || 'Something went wrong.' }]);
    } catch {
      setMessages(prev => [...prev, { role: 'assistant', content: 'Connection failed.' }]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {/* Multicolour glow FAB */}
      <AnimatePresence>
        {!open && (
          <motion.div
            initial={{ scale: 0, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0, opacity: 0 }}
            className="fixed bottom-6 right-6 z-50"
          >
            {/* Context-aware bubble */}
            <AnimatePresence>
              {showBubble && !bubbleDismissed && (
                <motion.div
                  initial={{ opacity: 0, y: 8, scale: 0.9 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  exit={{ opacity: 0, y: 8, scale: 0.9 }}
                  className="absolute bottom-full right-0 mb-3 w-56 p-3 rounded-xl border border-border bg-background shadow-lg"
                >
                  <button
                    onClick={() => { setBubbleDismissed(true); setShowBubble(false); }}
                    className="absolute top-2 right-2 text-muted-foreground hover:text-foreground"
                  >
                    <X className="w-3 h-3" />
                  </button>
                  <div className="flex items-start gap-2">
                    <div className="w-5 h-5 rounded-md bg-foreground flex items-center justify-center flex-shrink-0 mt-0.5">
                      <Bot className="w-2.5 h-2.5 text-background" strokeWidth={2.5} />
                    </div>
                    <p className="text-[11px] text-muted-foreground leading-relaxed pr-3">
                      {pageCtx.hint}
                    </p>
                  </div>
                  <div className="absolute bottom-0 right-6 translate-y-1/2 rotate-45 w-2.5 h-2.5 bg-background border-r border-b border-border" />
                </motion.div>
              )}
            </AnimatePresence>

            {/* Siri-style glow */}
            <div className="relative">
              <motion.div
                className="absolute inset-0 rounded-full blur-xl"
                style={{ width: 56, height: 56, left: -6, top: -6 }}
                animate={{
                  background: [
                    'radial-gradient(circle, rgba(245,158,11,0.35) 0%, rgba(168,85,247,0.25) 50%, rgba(59,130,246,0.2) 100%)',
                    'radial-gradient(circle, rgba(59,130,246,0.35) 0%, rgba(245,158,11,0.25) 50%, rgba(168,85,247,0.2) 100%)',
                    'radial-gradient(circle, rgba(168,85,247,0.35) 0%, rgba(59,130,246,0.25) 50%, rgba(245,158,11,0.2) 100%)',
                    'radial-gradient(circle, rgba(245,158,11,0.35) 0%, rgba(168,85,247,0.25) 50%, rgba(59,130,246,0.2) 100%)',
                  ],
                }}
                transition={{ duration: 4, repeat: Infinity, ease: 'easeInOut' }}
              />
              <motion.div
                className="absolute inset-0 rounded-full blur-2xl opacity-60"
                style={{ width: 72, height: 72, left: -14, top: -14 }}
                animate={{
                  background: [
                    'radial-gradient(circle, rgba(16,185,129,0.2) 0%, rgba(239,68,68,0.15) 50%, transparent 100%)',
                    'radial-gradient(circle, rgba(239,68,68,0.2) 0%, rgba(245,158,11,0.15) 50%, transparent 100%)',
                    'radial-gradient(circle, rgba(245,158,11,0.2) 0%, rgba(16,185,129,0.15) 50%, transparent 100%)',
                    'radial-gradient(circle, rgba(16,185,129,0.2) 0%, rgba(239,68,68,0.15) 50%, transparent 100%)',
                  ],
                }}
                transition={{ duration: 5, repeat: Infinity, ease: 'easeInOut' }}
              />
              <Button
                onClick={() => setOpen(true)}
                className="relative h-11 rounded-full shadow-lg bg-foreground hover:bg-foreground/90 px-5 gap-2 z-10"
                data-testid="spectre-fab"
              >
                <Bot className="w-4 h-4 text-background" strokeWidth={2} />
                <span className="text-[12px] font-medium text-background">Ask Specter</span>
              </Button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Panel */}
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 16, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 16, scale: 0.95 }}
            transition={{ duration: 0.2 }}
            className="fixed bottom-6 right-6 z-50 w-[380px] max-h-[520px] rounded-2xl border border-border bg-background shadow-2xl flex flex-col overflow-hidden"
            data-testid="spectre-assistant-panel"
          >
            {/* Header */}
            <div className="flex items-center justify-between px-4 py-3 border-b border-border">
              <div className="flex items-center gap-2">
                <div className="w-6 h-6 rounded-md bg-foreground flex items-center justify-center">
                  <Bot className="w-3 h-3 text-background" strokeWidth={2.5} />
                </div>
                <div>
                  <p className="text-xs font-semibold">Specter</p>
                  <p className="text-[9px] text-muted-foreground">{pageCtx.label} assistant</p>
                </div>
              </div>
              <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => setOpen(false)} data-testid="close-spectre-assistant">
                <X className="w-3.5 h-3.5" strokeWidth={2} />
              </Button>
            </div>

            {/* Messages */}
            <div className="flex-1 overflow-y-auto px-4 py-3 space-y-3 min-h-[200px]">
              {messages.length === 0 && (
                <div className="space-y-2">
                  <p className="text-xs text-muted-foreground mb-2">{pageCtx.hint}</p>
                  <p className="text-[10px] text-muted-foreground/70 mb-3">Try asking:</p>
                  {suggestedPrompts.slice(0, 3).map((p) => (
                    <button
                      key={p}
                      onClick={() => send(p)}
                      className="block w-full text-left px-3 py-2 rounded-lg border border-border/50 text-[11px] text-muted-foreground hover:text-foreground hover:border-border transition-all"
                    >
                      {p}
                    </button>
                  ))}
                </div>
              )}
              {messages.map((m, i) => (
                <div key={`${m.role}-${i}`} className={`flex gap-2 ${m.role === 'user' ? 'justify-end' : ''}`}>
                  {m.role === 'assistant' && (
                    <div className="w-5 h-5 rounded-md bg-foreground flex items-center justify-center flex-shrink-0 mt-0.5">
                      <Bot className="w-2.5 h-2.5 text-background" strokeWidth={2.5} />
                    </div>
                  )}
                  <div className={`max-w-[80%] px-3 py-2 rounded-xl text-[11px] leading-relaxed ${
                    m.role === 'user'
                      ? 'bg-foreground text-background'
                      : 'bg-secondary text-foreground'
                  }`}>
                    {m.content}
                  </div>
                </div>
              ))}
              {loading && (
                <div className="flex gap-2">
                  <div className="w-5 h-5 rounded-md bg-foreground flex items-center justify-center flex-shrink-0">
                    <Bot className="w-2.5 h-2.5 text-background" strokeWidth={2.5} />
                  </div>
                  <div className="flex items-center gap-1 px-3 py-2">
                    <div className="w-1.5 h-1.5 rounded-full bg-muted-foreground/40 animate-pulse" />
                    <div className="w-1.5 h-1.5 rounded-full bg-muted-foreground/40 animate-pulse" style={{ animationDelay: '150ms' }} />
                    <div className="w-1.5 h-1.5 rounded-full bg-muted-foreground/40 animate-pulse" style={{ animationDelay: '300ms' }} />
                  </div>
                </div>
              )}
              <div ref={endRef} />
            </div>

            {/* Disclaimer */}
            <div className="px-4 py-1.5 flex items-center gap-1.5 text-[9px] text-muted-foreground/50 border-t border-border/50">
              <ShieldAlert className="w-3 h-3 flex-shrink-0" strokeWidth={1.5} />
              Chats are monitored for safety and compliance purposes.
            </div>

            {/* Input */}
            <div className="px-3 py-2.5 border-t border-border">
              <form onSubmit={e => { e.preventDefault(); send(); }} className="flex gap-2">
                <input
                  ref={inputRef}
                  value={input}
                  onChange={e => setInput(e.target.value)}
                  placeholder="Ask Specter..."
                  className="flex-1 bg-secondary rounded-lg px-3 py-2 text-[11px] placeholder:text-muted-foreground/50 focus:outline-none focus:ring-1 focus:ring-ring"
                  data-testid="spectre-assist-input"
                />
                <Button type="submit" size="icon" className="h-8 w-8 rounded-lg" disabled={!input.trim() || loading} data-testid="spectre-assist-send">
                  {loading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Send className="w-3 h-3" />}
                </Button>
              </form>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
